import { Injectable } from '@angular/core';
import { Device, DeviceInfo } from '@capacitor/device';

/**
 * Single gateway for Capacitor Device plugin data.
 *
 * `getDeviceId()` returns the stable identifier used by the login allowlist gate
 * and analytics. `getDeviceInfo()` returns the full platform/model/name payload
 * used by push registration. Both calls are memoised for the lifetime of the
 * app instance since neither value changes within a run.
 */
@Injectable({ providedIn: 'root' })
export class DeviceIdService {
    private cachedId: string | null = null;
    private idInflight: Promise<string> | null = null;

    private cachedInfo: DeviceInfo | null = null;
    private infoInflight: Promise<DeviceInfo> | null = null;

    async getDeviceId(): Promise<string> {
        if (this.cachedId) {
            return this.cachedId;
        }
        if (!this.idInflight) {
            this.idInflight = Device.getId()
                .then((info) => {
                    this.cachedId = info.identifier;
                    return this.cachedId;
                })
                .catch((err) => {
                    this.idInflight = null;
                    throw err;
                });
        }
        return this.idInflight;
    }

    async getDeviceInfo(): Promise<DeviceInfo> {
        if (this.cachedInfo) {
            return this.cachedInfo;
        }
        if (!this.infoInflight) {
            this.infoInflight = Device.getInfo()
                .then((info) => {
                    this.cachedInfo = info;
                    return info;
                })
                .catch((err) => {
                    this.infoInflight = null;
                    throw err;
                });
        }
        return this.infoInflight;
    }

    reset(): void {
        this.cachedId = null;
        this.idInflight = null;
        this.cachedInfo = null;
        this.infoInflight = null;
    }
}
